import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, PawPrint, Stethoscope, HeartPulse } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 90% -10%, rgba(30,95,110,0.10), transparent 60%), linear-gradient(180deg, #FAF7F2 0%, #EFE9DF 100%)",
      }}
    >
      <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
      <div className="container-app relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center pt-12 pb-20 md:pt-16 md:pb-28 lg:pt-24 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-[#1E5F6E]/10 px-3.5 py-1.5 shadow-sm whitespace-nowrap"
            data-testid="hero-rating-badge"
          >
            <Star className="h-3.5 w-3.5 fill-[#E8A87C] text-[#E8A87C]" />
            <span className="text-[13px] font-semibold text-[#1A1A1A]">
              4,5/5 no Google
            </span>
            <span className="text-[13px] text-[#5A5A5A]">• 86 avaliações</span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] font-extrabold text-[#1A1A1A] leading-[1.05] tracking-tight"
          >
            Veterinários por{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1E5F6E]">profissão</span>
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 right-0 h-3 bg-[#E8A87C]/40 -z-0 rounded-sm"
              />
            </span>
            , cuidadores por vocação.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl text-lg md:text-xl text-[#5A5A5A] leading-relaxed"
          >
            Duas portas abertas na Madeira — Ribeira Brava e Calheta. Cuidamos do seu companheiro com a mesma proximidade e dedicação desde 2006. Marque online em menos de um minuto.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/marcar-consulta"
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1E5F6E] px-7 py-4 text-base font-bold text-white shadow-[0_10px_25px_-10px_rgba(30,95,110,0.6)] hover:bg-[#174E5B] hover:-translate-y-0.5 hover:shadow-[0_16px_35px_-12px_rgba(30,95,110,0.6)] transition-all"
            >
              Marcar consulta
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/sobre"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#1E5F6E]/15 bg-white px-7 py-4 text-base font-bold text-[#1E5F6E] hover:border-[#1E5F6E]/40 hover:-translate-y-0.5 transition-all"
            >
              Conhecer a clínica
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#5A5A5A]"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#88A096]" />
              Duas clínicas na Madeira
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#88A096]" />
              Equipa próxima e dedicada
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#88A096]" />
              Marcação 24/7
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
          data-testid="hero-illustration"
        >
          <div className="relative aspect-[4/4.4] max-w-lg mx-auto">
            {/* Background blob */}
            <div className="absolute inset-0 rounded-[40px] bg-gradient-to-br from-[#88A096]/30 via-[#88A096]/15 to-[#E8A87C]/20" />
            <div className="absolute inset-3 rounded-[34px] bg-white/70 backdrop-blur-sm border border-white shadow-[0_30px_60px_-30px_rgba(15,58,68,0.35)]" />

            {/* Flat illustration: stylized dog + cat */}
            <svg
              viewBox="0 0 400 440"
              className="absolute inset-0 w-full h-full p-8"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <defs>
                <linearGradient id="floorGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#88A096" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#88A096" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Floor shadow */}
              <ellipse cx="200" cy="380" rx="150" ry="14" fill="url(#floorGrad)" />

              {/* Dog (left) */}
              <g>
                {/* Body */}
                <path
                  d="M70 320 Q60 250 120 240 Q150 220 195 235 Q230 245 230 295 L230 340 Q230 360 210 360 L90 360 Q70 360 70 340 Z"
                  fill="#E8A87C"
                />
                {/* Belly */}
                <path
                  d="M105 315 Q110 285 165 290 Q210 295 210 330 L210 355 L105 355 Z"
                  fill="#F4C29B"
                />
                {/* Head */}
                <circle cx="135" cy="200" r="58" fill="#E8A87C" />
                {/* Snout */}
                <ellipse cx="135" cy="220" rx="28" ry="20" fill="#F4C29B" />
                {/* Ears */}
                <path d="M85 165 Q70 195 95 215 Q115 198 110 175 Z" fill="#C98A66" />
                <path d="M185 165 Q200 195 175 215 Q155 198 160 175 Z" fill="#C98A66" />
                {/* Eyes */}
                <circle cx="118" cy="195" r="5" fill="#1A1A1A" />
                <circle cx="152" cy="195" r="5" fill="#1A1A1A" />
                <circle cx="119" cy="193" r="1.5" fill="#fff" />
                <circle cx="153" cy="193" r="1.5" fill="#fff" />
                {/* Nose */}
                <ellipse cx="135" cy="217" rx="6" ry="5" fill="#1A1A1A" />
                {/* Mouth */}
                <path
                  d="M135 226 Q130 234 122 232 M135 226 Q140 234 148 232"
                  stroke="#1A1A1A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
                {/* Collar */}
                <rect x="95" y="252" width="80" height="10" rx="3" fill="#1E5F6E" />
                <circle cx="135" cy="262" r="6" fill="#E8A87C" stroke="#1E5F6E" strokeWidth="2" />
                {/* Paws */}
                <rect x="100" y="350" width="22" height="14" rx="6" fill="#C98A66" />
                <rect x="180" y="350" width="22" height="14" rx="6" fill="#C98A66" />
              </g>

              {/* Cat (right) */}
              <g>
                {/* Body */}
                <path
                  d="M250 360 Q240 290 280 280 Q310 270 335 285 Q360 305 358 340 Q356 365 330 365 L260 365 Q250 365 250 360 Z"
                  fill="#1E5F6E"
                />
                {/* Belly */}
                <path
                  d="M275 350 Q275 320 310 318 Q345 320 345 350 L275 355 Z"
                  fill="#88A096"
                />
                {/* Head */}
                <circle cx="305" cy="250" r="46" fill="#1E5F6E" />
                {/* Ears */}
                <path d="M268 218 L262 188 L292 215 Z" fill="#1E5F6E" />
                <path d="M342 218 L348 188 L318 215 Z" fill="#1E5F6E" />
                <path d="M275 213 L275 198 L286 213 Z" fill="#E8A87C" />
                <path d="M335 213 L335 198 L324 213 Z" fill="#E8A87C" />
                {/* Eyes */}
                <ellipse cx="290" cy="248" rx="5" ry="7" fill="#FAF7F2" />
                <ellipse cx="320" cy="248" rx="5" ry="7" fill="#FAF7F2" />
                <ellipse cx="290" cy="250" rx="2" ry="4" fill="#1A1A1A" />
                <ellipse cx="320" cy="250" rx="2" ry="4" fill="#1A1A1A" />
                {/* Nose */}
                <path d="M302 262 L308 262 L305 267 Z" fill="#E8A87C" />
                {/* Mouth */}
                <path
                  d="M305 267 Q302 273 296 271 M305 267 Q308 273 314 271"
                  stroke="#FAF7F2"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
                {/* Whiskers */}
                <line x1="282" y1="266" x2="265" y2="262" stroke="#FAF7F2" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="282" y1="270" x2="266" y2="272" stroke="#FAF7F2" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="328" y1="266" x2="345" y2="262" stroke="#FAF7F2" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="328" y1="270" x2="344" y2="272" stroke="#FAF7F2" strokeWidth="1.5" strokeLinecap="round" />
                {/* Tail */}
                <path
                  d="M358 320 Q395 305 380 270 Q370 250 355 260"
                  stroke="#1E5F6E"
                  strokeWidth="14"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>

              {/* Heart between */}
              <path
                d="M200 142 Q190 130 178 138 Q166 148 180 162 L200 180 L220 162 Q234 148 222 138 Q210 130 200 142 Z"
                fill="#E8A87C"
                opacity="0.95"
              />
            </svg>

            {/* Floating chips */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="absolute -left-2 top-12 rounded-2xl bg-white border border-[#1E5F6E]/10 shadow-lg px-3.5 py-2.5 flex items-center gap-2"
            >
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#1E5F6E]/10 text-[#1E5F6E]">
                <Stethoscope className="h-4 w-4" />
              </span>
              <div className="text-left">
                <div className="text-[11px] font-medium text-[#5A5A5A] uppercase tracking-wide">
                  Próxima consulta
                </div>
                <div className="text-sm font-bold text-[#1A1A1A]">
                  Hoje • 18:30
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="absolute -right-3 bottom-10 rounded-2xl bg-white border border-[#1E5F6E]/10 shadow-lg px-3.5 py-2.5 flex items-center gap-2"
            >
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#E8A87C]/20 text-[#1E5F6E]">
                <HeartPulse className="h-4 w-4" />
              </span>
              <div className="text-left">
                <div className="text-[11px] font-medium text-[#5A5A5A] uppercase tracking-wide">
                  Urgência 24h
                </div>
                <div className="text-sm font-bold text-[#1A1A1A]">
                  Ligue 291 676 284
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-[#1E5F6E] text-white px-4 py-1.5 text-xs font-semibold shadow-lg flex items-center gap-1.5"
            >
              <PawPrint className="h-3.5 w-3.5" />
              Madeira • Ribeira Brava + Calheta
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
